import React, { useState } from 'react';
import { useSystemLogs } from '../services/hooks/useSystemLogs';
import { systemLogsService } from '../services/systemLogsService';
import JsonView from './common/JsonView';

export function SystemLogsPage() {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    level: '',
    domain: '',
    eventType: '',
    logType: '',
    sourceId: '',
  });

  const {
    logs,
    total,
    hasMore,
    isLoading,
    error,
    updateQuery,
    loadMore,
    resetQuery,
  } = useSystemLogs({ limit: 50 });

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const query: any = { limit: 50 };
    
    if (filters.startDate) query.startDate = filters.startDate;
    if (filters.endDate) query.endDate = filters.endDate;
    if (filters.level) query.level = filters.level as any;
    if (filters.domain) query.domain = filters.domain;
    if (filters.eventType) query.eventType = filters.eventType;
    if (filters.logType) query.logType = filters.logType;
    if (filters.sourceId) query.sourceId = filters.sourceId;

    updateQuery(query);
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      level: '',
      domain: '',
      eventType: '',
      logType: '',
      sourceId: '',
    });
    resetQuery();
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Error loading system logs</h3>
          <p className="text-red-600 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">System Logs</h1>
        <p className="text-gray-600 mt-1">
          View property processing activity, manual triggers, and system events across all domains
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="datetime-local"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="datetime-local"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Level
            </label>
            <select
              value={filters.level}
              onChange={(e) => handleFilterChange('level', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Levels</option>
              {systemLogsService.getAvailableLevels().map(level => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          {/* Domain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain
            </label>
            <select
              value={filters.domain}
              onChange={(e) => handleFilterChange('domain', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Domains</option>
              {systemLogsService.getAvailableDomains().map(domain => (
                <option key={domain} value={domain}>
                  {systemLogsService.getDomainDisplayName(domain)}
                </option>
              ))}
            </select>
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Type
            </label>
            <input
              type="text"
              value={filters.eventType}
              onChange={(e) => handleFilterChange('eventType', e.target.value)}
              placeholder="e.g., SOURCE_PROCESSING_FAILED"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Log Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Log Type
            </label>
            <input
              type="text"
              value={filters.logType}
              onChange={(e) => handleFilterChange('logType', e.target.value)}
              placeholder="e.g., PROPERTY_CHANGE"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Source ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source ID
            </label>
            <input
              type="text"
              value={filters.sourceId}
              onChange={(e) => handleFilterChange('sourceId', e.target.value)}
              placeholder="e.g., source-123"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Apply Filters
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              System Logs ({total} total)
            </h2>
            {isLoading && (
              <div className="text-sm text-gray-500">Loading...</div>
            )}
          </div>
        </div>

        {logs.length === 0 && !isLoading ? (
          <div className="p-8 text-center text-gray-500">
            No system logs found matching the current filters.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log) => (
              <SystemLogItem key={log.id} log={log} />
            ))}
          </div>
        )}

        {hasMore && (
          <div className="px-4 py-3 border-t border-gray-200">
            <button
              onClick={loadMore}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface SystemLogItemProps {
  log: any;
}

function SystemLogItem({ log }: SystemLogItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="p-4 hover:bg-gray-50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">
              {systemLogsService.getSeverityIcon(log.severity)}
            </span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${systemLogsService.getSeverityColor(log.severity)}`}>
              {log.level || log.severity}
            </span>
            <span className="text-sm text-gray-500">
              {systemLogsService.getDomainDisplayName(log.domain)}
            </span>
          </div>

          <h3 className="font-medium text-gray-900 mb-1">
            {log.eventType}
          </h3>

          {log.logType && (
            <p className="text-xs uppercase text-gray-500 mb-1">{log.logType}</p>
          )}

          <p className="text-gray-700 mb-2">
            {log.message || 'No description provided'}
          </p>

          <div className="text-sm text-gray-500 space-y-1">
            <div>Source ID: {log.sourceId}</div>
            <div>Timestamp: {systemLogsService.formatTimestamp(log.timestamp)}</div>
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ml-4 text-gray-400 hover:text-gray-600"
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Additional Context</h4>
          <JsonView data={log.additionalContext} />
        </div>
      )}
    </div>
  );
} 